/**
 * Fetch wrapper that always sends cookies and parses JSON.
 * Throws on non-2xx with the response body attached.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
  }
}

/**
 * Build the absolute API URL.
 * - In dev (Vite proxy), VITE_API_URL is empty → use relative path → proxy handles it.
 * - In prod, VITE_API_URL=https://api.dhc-track.io → calls the API host directly.
 */
function buildUrl(path: string): string {
  const base = import.meta.env["VITE_API_URL"] ?? "";
  return base ? `${base.replace(/\/$/, "")}${path}` : path;
}

/** Public helper for non-fetch URLs (e.g. OAuth redirects in <a href>). */
export function apiUrl(path: string): string {
  return buildUrl(path);
}

export async function api<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  if (init.body !== undefined && init.body !== null) {
    headers["Content-Type"] = "application/json";
  }
  const res = await fetch(buildUrl(path), {
    ...init,
    credentials: "include",
    headers: { ...headers, ...(init.headers || {}) },
  });

  const isJson = res.headers.get("content-type")?.includes("application/json");
  const body = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    throw new ApiError(res.status, `${res.status} ${res.statusText}`, body);
  }
  return body as T;
}
